import { lazy, Suspense, useState, type ComponentType } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { type AdultAccessMode } from './features/adult-access/AdultAccessApp';
import { RequireRole } from './features/adult-access/RequireRole';
import { IdleSignOut } from './features/adult-access/IdleSignOut';
import { RequireActiveSession } from './features/adult-access/RequireActiveSession';
import { LandingPage } from './features/marketing/LandingPage';
import { SupportMessageContext } from './features/adult-access/SupportMessageContext';
import { createSupportMessagesService } from './platform/supabase/support-messages-data';
import { sendHelpMessageEmail } from './platform/help-message-email';
import { TeacherShellContext } from './features/teacher-planning/teacherShellContext';
import {
  sendSchoolRequestEmail,
  type SchoolRequestEmailKind,
} from './platform/school-invite-email';
import { AlertsProviderContext } from './features/teacher-planning/teacherAlertsContext';
import { RequireLearnerCapability } from './features/learner-access/RequireLearnerCapability';
import { CompletionState } from './features/learner-practice/CompletionState';
import { EndEarlyState } from './features/learner-practice/EndEarlyState';
import { FocusMoment } from './features/learner-practice/FocusMoment';
import { HelpState } from './features/learner-practice/HelpState';
import { PausedState } from './features/learner-practice/PausedState';
import { PracticeTask } from './features/learner-practice/PracticeTask';
import { ProblemReportState } from './features/learner-practice/ProblemReportState';
import { SessionIntroduction } from './features/learner-practice/SessionIntroduction';
import { StudentStart } from './features/learner-practice/StudentStart';
import { createAdultAuthService } from './platform/auth/adult-auth';
import { parseBrowserEnv, readBrowserEnv } from './platform/config/env';
import { createLearnerSessionClient } from './platform/learner/learner-session-client';
import {
  createAdultDataService,
  type SupabaseDataProvider,
} from './platform/supabase/adult-data';
import { createBrowserSupabaseClient } from './platform/supabase/client';
import { createHomeSchoolConnectionService } from './platform/supabase/home-school-connection-data';
import { CanonicalLink } from './components/CanonicalLink';
import { ScrollToTop } from './components/ScrollToTop';
import { SafeExit } from './shells/SafeExit';
import { StatePage } from './states/StatePage';

// After a new version goes live, a tab opened earlier still asks for the
// old page files, which no longer exist. Reload once to pick up the new
// version instead of failing (2026-10-02).
const RELOADED_KEY = 'dodoskills.reloadedForNewVersion';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyPage<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    load().then(
      (module) => {
        try {
          sessionStorage.removeItem(RELOADED_KEY);
        } catch {
          // Storage blocked: nothing to clear.
        }
        return module;
      },
      (error: unknown) => {
        let reloaded = false;
        try {
          reloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
          if (!reloaded) sessionStorage.setItem(RELOADED_KEY, '1');
        } catch {
          reloaded = true;
        }
        if (reloaded) throw error;
        window.location.reload();
        return new Promise<{ default: T }>(() => undefined);
      },
    ),
  );
}

// Pages load on demand, so a student opening /learn (or a visitor on the
// homepage) downloads only what that page needs (test report item 26).
const AdultAccessApp = lazyPage(() =>
  import('./features/adult-access/AdultAccessApp').then((m) => ({
    default: m.AdultAccessApp,
  })),
);
const ConnectToSchool = lazyPage(() =>
  import('./features/adult-access/ConnectToSchool').then((m) => ({
    default: m.ConnectToSchool,
  })),
);
const ParentChildResults = lazyPage(() =>
  import('./features/adult-access/ParentChildResults').then((m) => ({
    default: m.ParentChildResults,
  })),
);
const Household = lazyPage(() =>
  import('./features/adult-access/Household').then((m) => ({
    default: m.Household,
  })),
);
const InviteTeacher = lazyPage(() =>
  import('./features/adult-access/InviteTeacher').then((m) => ({
    default: m.InviteTeacher,
  })),
);
const ParentOnboarding = lazyPage(() =>
  import('./features/adult-access/ParentOnboarding').then((m) => ({
    default: m.ParentOnboarding,
  })),
);
const TeacherOnboarding = lazyPage(() =>
  import('./features/adult-access/TeacherOnboarding').then((m) => ({
    default: m.TeacherOnboarding,
  })),
);
const GuideArticle = lazyPage(() =>
  import('./features/guide/GuidePages').then((m) => ({
    default: m.GuideArticle,
  })),
);
const GuideIndex = lazyPage(() =>
  import('./features/guide/GuidePages').then((m) => ({
    default: m.GuideIndex,
  })),
);
const AccessibilityPage = lazyPage(() =>
  import('./features/marketing/FooterPages').then((m) => ({
    default: m.AccessibilityPage,
  })),
);
const ContactPage = lazyPage(() =>
  import('./features/marketing/FooterPages').then((m) => ({
    default: m.ContactPage,
  })),
);
const PublicPrivacyPage = lazyPage(() =>
  import('./features/marketing/FooterPages').then((m) => ({
    default: m.PublicPrivacyPage,
  })),
);
const PublicTermsPage = lazyPage(() =>
  import('./features/marketing/FooterPages').then((m) => ({
    default: m.PublicTermsPage,
  })),
);
const SafeguardingPage = lazyPage(() =>
  import('./features/marketing/FooterPages').then((m) => ({
    default: m.SafeguardingPage,
  })),
);
const ActiveSessionMonitor = lazyPage(() =>
  import('./features/teacher-planning/ActiveSessionMonitor').then((m) => ({
    default: m.ActiveSessionMonitor,
  })),
);
const ApproveParentGuidance = lazyPage(() =>
  import('./features/teacher-planning/ApproveParentGuidance').then((m) => ({
    default: m.ApproveParentGuidance,
  })),
);
const AssignmentSuccess = lazyPage(() =>
  import('./features/teacher-planning/AssignmentSuccess').then((m) => ({
    default: m.AssignmentSuccess,
  })),
);
const AssignWorkForm = lazyPage(() =>
  import('./features/teacher-planning/AssignWorkForm').then((m) => ({
    default: m.AssignWorkForm,
  })),
);
const TeacherCalendar = lazyPage(() =>
  import('./features/teacher-planning/TeacherCalendar').then((m) => ({
    default: m.TeacherCalendar,
  })),
);
const ClassEvidenceReview = lazyPage(() =>
  import('./features/teacher-planning/ClassEvidenceReview').then((m) => ({
    default: m.ClassEvidenceReview,
  })),
);
const TeacherDrafts = lazyPage(() =>
  import('./features/teacher-planning/TeacherDrafts').then((m) => ({
    default: m.TeacherDrafts,
  })),
);
const HomeTeacherInvitations = lazyPage(() =>
  import('./features/teacher-planning/HomeTeacherInvitations').then((m) => ({
    default: m.HomeTeacherInvitations,
  })),
);
const CurriculumCompletion = lazyPage(() =>
  import('./features/teacher-planning/CurriculumCompletion').then((m) => ({
    default: m.CurriculumCompletion,
  })),
);
const IndividualStudentResult = lazyPage(() =>
  import('./features/teacher-planning/IndividualStudentResult').then((m) => ({
    default: m.IndividualStudentResult,
  })),
);
const InterventionRecord = lazyPage(() =>
  import('./features/teacher-planning/InterventionRecord').then((m) => ({
    default: m.InterventionRecord,
  })),
);
const LearningIntentSetup = lazyPage(() =>
  import('./features/teacher-planning/LearningIntentSetup').then((m) => ({
    default: m.LearningIntentSetup,
  })),
);
const LocalLearnerKeyExplainer = lazyPage(() =>
  import('./features/teacher-planning/LocalLearnerKeyExplainer').then((m) => ({
    default: m.LocalLearnerKeyExplainer,
  })),
);
const PracticePlanBuilder = lazyPage(() =>
  import('./features/teacher-planning/PracticePlanBuilder').then((m) => ({
    default: m.PracticePlanBuilder,
  })),
);
const PracticePlanForIntent = lazyPage(() =>
  import('./features/teacher-planning/PracticePlanForIntent').then((m) => ({
    default: m.PracticePlanForIntent,
  })),
);
const PracticePreviewApproval = lazyPage(() =>
  import('./features/teacher-planning/PracticePreviewApproval').then((m) => ({
    default: m.PracticePreviewApproval,
  })),
);
const ParentResponseReview = lazyPage(() =>
  import('./features/teacher-planning/ParentResponseReview').then((m) => ({
    default: m.ParentResponseReview,
  })),
);
const PilotSupport = lazyPage(() =>
  import('./features/teacher-planning/PilotSupport').then((m) => ({
    default: m.PilotSupport,
  })),
);
const ActivityCodeSheet = lazyPage(() =>
  import('./features/teacher-planning/ActivityCodeSheet').then((m) => ({
    default: m.ActivityCodeSheet,
  })),
);
const TeacherSearch = lazyPage(() =>
  import('./features/teacher-planning/TeacherSearch').then((m) => ({
    default: m.TeacherSearch,
  })),
);
const PrivacyPolicy = lazyPage(() =>
  import('./features/teacher-planning/PrivacyPolicy').then((m) => ({
    default: m.PrivacyPolicy,
  })),
);
const ResultsAnalysis = lazyPage(() =>
  import('./features/teacher-planning/ResultsAnalysis').then((m) => ({
    default: m.ResultsAnalysis,
  })),
);
const TaskDetail = lazyPage(() =>
  import('./features/teacher-planning/TaskDetail').then((m) => ({
    default: m.TaskDetail,
  })),
);
const SkillMap = lazyPage(() =>
  import('./features/teacher-planning/SkillMap').then((m) => ({
    default: m.SkillMap,
  })),
);
const MasterSkillTree = lazyPage(() =>
  import('./features/teacher-planning/MasterSkillTree').then((m) => ({
    default: m.MasterSkillTree,
  })),
);
const StudentCompetencyReview = lazyPage(() =>
  import('./features/teacher-planning/StudentCompetencyReview').then((m) => ({
    default: m.StudentCompetencyReview,
  })),
);
const StudentPerformanceList = lazyPage(() =>
  import('./features/teacher-planning/StudentPerformanceList').then((m) => ({
    default: m.StudentPerformanceList,
  })),
);
const StudentAnalyticsShell = lazyPage(() =>
  import('./features/teacher-planning/StudentAnalyticsShell').then((m) => ({
    default: m.StudentAnalyticsShell,
  })),
);
const StudentConnectionCodes = lazyPage(() =>
  import('./features/teacher-planning/StudentConnectionCodes').then((m) => ({
    default: m.StudentConnectionCodes,
  })),
);
const StudentConnectionCodesPrint = lazyPage(() =>
  import('./features/teacher-planning/StudentConnectionCodesPrint').then(
    (m) => ({ default: m.StudentConnectionCodesPrint }),
  ),
);
const StudentOverview = lazyPage(() =>
  import('./features/teacher-planning/StudentOverview').then((m) => ({
    default: m.StudentOverview,
  })),
);
const TeacherHome = lazyPage(() =>
  import('./features/teacher-planning/TeacherHome').then((m) => ({
    default: m.TeacherHome,
  })),
);
const StaffHelpMessages = lazyPage(() =>
  import('./features/staff/StaffHelpMessages').then((m) => ({
    default: m.StaffHelpMessages,
  })),
);
const SchoolAccountReview = lazyPage(() =>
  import('./features/school-accounts/SchoolAccountReview').then((m) => ({
    default: m.SchoolAccountReview,
  })),
);
const SchoolAccountSetup = lazyPage(() =>
  import('./features/school-accounts/SchoolAccountSetup').then((m) => ({
    default: m.SchoolAccountSetup,
  })),
);
const SchoolAdminHome = lazyPage(() =>
  import('./features/school-admin/SchoolAdminHome').then((m) => ({
    default: m.SchoolAdminHome,
  })),
);
const SchoolAdminTeachers = lazyPage(() =>
  import('./features/school-admin/SchoolAdminTeachers').then((m) => ({
    default: m.SchoolAdminTeachers,
  })),
);
const SchoolAdminReports = lazyPage(() =>
  import('./features/school-admin/SchoolAdminReports').then((m) => ({
    default: m.SchoolAdminReports,
  })),
);
const SchoolLink = lazyPage(() =>
  import('./features/school-accounts/SchoolLink').then((m) => ({
    default: m.SchoolLink,
  })),
);
const YourAccount = lazyPage(() =>
  import('./features/adult-access/YourAccount').then((m) => ({
    default: m.YourAccount,
  })),
);
const StudentPersonalCodes = lazyPage(() =>
  import('./features/adult-access/StudentPersonalCodes').then((m) => ({
    default: m.StudentPersonalCodes,
  })),
);
const TermsAndConditions = lazyPage(() =>
  import('./features/teacher-planning/TermsAndConditions').then((m) => ({
    default: m.TermsAndConditions,
  })),
);

type AppProps = { browserEnvironment?: unknown };

// Thin route wrapper: reads the :teacherStudentId path param and passes it
// through as StudentAnalyticsShell's initial-selection prop, so
// StudentAnalyticsShell itself stays route-agnostic and easy to test with
// plain props.
function StudentAnalyticsRoute(
  props: Omit<
    Parameters<typeof StudentAnalyticsShell>[0],
    'initialTeacherStudentId'
  >,
) {
  const { teacherStudentId } = useParams<{ teacherStudentId: string }>();
  return (
    <StudentAnalyticsShell
      {...props}
      initialTeacherStudentId={teacherStudentId}
    />
  );
}

export function App({ browserEnvironment }: AppProps = {}) {
  // Hooks must run unconditionally on every render, so environment
  // resolution (which can fail) happens inside the lazy useState
  // initializer rather than before the hook call.
  const [services] = useState(() => {
    try {
      const environment =
        browserEnvironment === undefined
          ? readBrowserEnv()
          : parseBrowserEnv(browserEnvironment);
      const client = createBrowserSupabaseClient(environment);
      const auth = createAdultAuthService(client.auth);
      const data = createAdultDataService(
        client as unknown as SupabaseDataProvider,
      );
      const teacherStudentsProvider = client as unknown as SupabaseDataProvider;
      const homeSchoolConnection = createHomeSchoolConnectionService(
        client as unknown as SupabaseDataProvider,
      );
      const learnerSessionClient = createLearnerSessionClient();
      return {
        ok: true as const,
        client,
        auth,
        data,
        teacherStudentsProvider,
        homeSchoolConnection,
        learnerSessionClient,
      };
    } catch {
      return { ok: false as const };
    }
  });

  if (!services.ok) return <StatePage state="system-error" />;

  const {
    auth,
    data,
    teacherStudentsProvider,
    homeSchoolConnection,
    learnerSessionClient,
  } = services;
  const getAccessToken = () =>
    services.client.auth
      .getSession()
      .then(({ data: session }) => session.session?.access_token ?? null);
  // Help messages: tell DodoSkills staff by email that one is waiting.
  // Best effort, never blocks the page.
  const notifyHelpMessage = () => {
    void getAccessToken().then((token) =>
      token ? sendHelpMessageEmail({ kind: 'new_message' }, token) : false,
    );
  };
  const baseSupportMessages = createSupportMessagesService(
    teacherStudentsProvider,
  );
  const supportMessages = {
    ...baseSupportMessages,
    submit: async (message: string, page: string) => {
      const result = await baseSupportMessages.submit(message, page);
      if (result.ok) notifyHelpMessage();
      return result;
    },
  };
  // School account request emails (S2): best effort, never blocks the page.
  const notifySchoolRequest = (
    requestId: string,
    kind: SchoolRequestEmailKind,
  ) => {
    void getAccessToken().then((token) =>
      token ? sendSchoolRequestEmail(requestId, kind, token) : false,
    );
  };
  const access = (mode: AdultAccessMode) => (
    <AdultAccessApp
      mode={mode}
      auth={auth}
      data={data}
      connections={homeSchoolConnection}
      homeWorkProvider={teacherStudentsProvider}
    />
  );

  return (
    <SupportMessageContext.Provider value={supportMessages}>
      <AlertsProviderContext.Provider value={teacherStudentsProvider}>
        {/* Signs adults out after 30 minutes without activity. */}
        <IdleSignOut auth={auth} />
        {/* Each new page opens at the top (phones kept the old scroll). */}
        <ScrollToTop />
        <CanonicalLink />
        <Suspense
          fallback={
            <p role="status" className="p-6 text-center text-muted">
              Loading…
            </p>
          }
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Public footer pages (landing-page footer links). */}
            <Route path="/privacy" element={<PublicPrivacyPage />} />
            <Route path="/terms" element={<PublicTermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* How to use DodoSkills (public guide centre). */}
            <Route path="/guide" element={<GuideIndex />} />
            <Route path="/guide/:slug" element={<GuideArticle />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/safeguarding" element={<SafeguardingPage />} />
            <Route path="/auth/sign-in" element={access('sign-in')} />
            {/* Sign Up / Log In split (2026-09-16): role is chosen here, before
          the account exists (see AdultSignUp) -- separate from Log In,
          which never creates an account. */}
            <Route path="/auth/sign-up" element={access('sign-up')} />
            {/* Kept as redirects, never a dead link, for anything already
          bookmarked from before either redesign. */}
            <Route
              path="/auth/start"
              element={<Navigate to="/auth/sign-up" replace />}
            />
            <Route
              path="/auth/register-parent"
              element={<Navigate to="/auth/sign-up" replace />}
            />
            <Route
              path="/auth/register-teacher"
              element={<Navigate to="/auth/sign-up" replace />}
            />
            <Route path="/auth/recovery" element={access('recovery')} />
            <Route path="/auth/callback" element={access('callback')} />
            <Route
              path="/adult/parent-home"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  {access('parent')}
                </RequireRole>
              }
            />
            <Route
              path="/adult/parent-home/results"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <ParentChildResults
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/parent-home/codes"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <StudentPersonalCodes
                    kind="parent"
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/parent-home/account"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <YourAccount kind="parent" auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/onboarding"
              element={
                <RequireActiveSession auth={auth}>
                  <TeacherOnboarding
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireActiveSession>
              }
            />
            <Route
              path="/adult/parent/onboarding"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <ParentOnboarding data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/household"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <Household provider={teacherStudentsProvider} auth={auth} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/invite-teacher"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <InviteTeacher auth={auth} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/role-selection"
              element={access('role-selection')}
            />
            <Route
              path="/adult/school-context"
              element={access('school-context')}
            />
            <Route
              path="/revoked-access"
              element={<StatePage state="revoked-connection" />}
            />
            {/* A parent scanning a printed QR code (Screen 13) or entering a
          student connection code lands here. Requires an active parent
          session, matching every other /adult/* screen -- redemption acts
          on the caller's own home learner spaces. */}
            <Route
              path="/connect"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <ConnectToSchool
                    data={data}
                    connections={homeSchoolConnection}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherHome
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            {/* School accounts Phase 2a: a school leader asks for a verified
          school account; DodoSkills staff check it by hand. The staff page's
          data is refused by the server to anyone without support_operations. */}
            <Route
              path="/adult/teacher/school-account"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <SchoolAccountSetup
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                    getAccessToken={getAccessToken}
                    notifyStaff={(requestId) => {
                      notifySchoolRequest(requestId, 'new_request');
                    }}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/student-codes"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentPersonalCodes
                    kind="teacher"
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/account"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <YourAccount kind="teacher" auth={auth} data={data} />
                </RequireRole>
              }
            />
            {/* Phase 2b: a teacher links their classes to a verified school. */}
            <Route
              path="/adult/teacher/school-link"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <SchoolLink
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/staff/messages"
              element={
                <RequireActiveSession auth={auth}>
                  <StaffHelpMessages
                    provider={teacherStudentsProvider}
                    auth={auth}
                    sendReplyEmail={(source, id) =>
                      getAccessToken().then((token) =>
                        token
                          ? sendHelpMessageEmail(
                              { kind: 'reply', source, id },
                              token,
                            )
                          : false,
                      )
                    }
                  />
                </RequireActiveSession>
              }
            />
            <Route
              path="/adult/staff/school-accounts"
              element={
                <RequireActiveSession auth={auth}>
                  <SchoolAccountReview
                    provider={teacherStudentsProvider}
                    auth={auth}
                    notifyLeader={(requestId) => {
                      notifySchoolRequest(requestId, 'decision');
                    }}
                  />
                </RequireActiveSession>
              }
            />
            <Route
              path="/adult/teacher/calendar"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherCalendar
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/assign"
              element={
                <RequireRole auth={auth} data={data} allow="teacher-or-parent">
                  <AssignWorkForm
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            {/* Parents reach the same form at a parent address (2026-10-01). */}
            <Route
              path="/adult/parent-home/assign"
              element={
                <RequireRole auth={auth} data={data} allow="parent">
                  <AssignWorkForm
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/assign/success"
              element={
                <RequireRole auth={auth} data={data} allow="teacher-or-parent">
                  <AssignmentSuccess auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/drafts"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherDrafts
                    provider={teacherStudentsProvider}
                    data={data}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            {/* Plans is hidden (2026-09-26): its "awaiting approval" list only
            repeated Scheduled work, and "create another plan" led into the
            old planning pages. Old links land on Scheduled work. */}
            <Route
              path="/adult/teacher/plans"
              element={<Navigate to="/adult/teacher/drafts" replace />}
            />
            <Route
              path="/adult/teacher/home-invitations"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <HomeTeacherInvitations
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/skill-map"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <SkillMap
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/skill-map/tree"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <MasterSkillTree auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/curriculum-completion"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <CurriculumCompletion
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/pilot-support"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <PilotSupport
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                    onReportFiled={notifyHelpMessage}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/:planId"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TaskDetail
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/:planId/results"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ResultsAnalysis
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/:planId/results/students"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentPerformanceList
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/:planId/results/students/:teacherStudentId"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <IndividualStudentResult
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/search"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherSearch
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/students"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentOverview
                    auth={auth}
                    data={data}
                    studentsProvider={teacherStudentsProvider}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/analytics"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentAnalyticsShell
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            {/* A static path, so React Router ranks it above the dynamic
          :teacherStudentId route below regardless of declaration order --
          this is the direct "Teaching groups" entry point from Teacher
          Home, matching the Analytics dashboard's own third tab. */}
            <Route
              path="/adult/teacher/analytics/groups"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentAnalyticsShell
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                    initialMode="teaching-groups"
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/analytics/:teacherStudentId"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentAnalyticsRoute
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/parent-guidance/approve"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ApproveParentGuidance auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/parent-guidance/responses"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ParentResponseReview auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/terms"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TermsAndConditions auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/privacy"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <PrivacyPolicy auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/students/codes"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentConnectionCodes
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/students/codes/print"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentConnectionCodesPrint
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/students/codes/learner-key"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <LocalLearnerKeyExplainer
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/intent/new"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherShellContext.Provider value={{ auth, data }}>
                    <LearningIntentSetup provider={teacherStudentsProvider} />
                  </TeacherShellContext.Provider>
                </RequireRole>
              }
            />
            {/* DM-IMP-006 remediation (Round 5 finding I-1) / final-review Product
          Office decision item 12: the durable, reload-safe entry point for
          creating another practice plan under an approved governing learning
          intent. The learning_intent id lives in the URL, and every
          authorization/governing fact is re-resolved server-side on load --
          unlike /adult/teacher/plan/new below, which is the same-session
          continuation of an authoring flow and carries its context in
          navigation state. */}
            <Route
              path="/adult/teacher/intent/:intentId/plan/new"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherShellContext.Provider value={{ auth, data }}>
                    <PracticePlanForIntent provider={teacherStudentsProvider} />
                  </TeacherShellContext.Provider>
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/new"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherShellContext.Provider value={{ auth, data }}>
                    <PracticePlanBuilder provider={teacherStudentsProvider} />
                  </TeacherShellContext.Provider>
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/plan/approve"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <TeacherShellContext.Provider value={{ auth, data }}>
                    <PracticePreviewApproval
                      provider={teacherStudentsProvider}
                    />
                  </TeacherShellContext.Provider>
                </RequireRole>
              }
            />
            {/* First wave T1: make and print a whole assignment's activity codes. */}
            <Route
              path="/adult/teacher/activity-codes"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ActivityCodeSheet
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/sessions"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ActiveSessionMonitor
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/evidence-review"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <ClassEvidenceReview
                    data={data}
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/students/:teacherStudentId/review"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <StudentCompetencyReview
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/teacher/interventions/:interventionId"
              element={
                <RequireRole auth={auth} data={data} allow="teacher">
                  <InterventionRecord
                    provider={teacherStudentsProvider}
                    auth={auth}
                    data={data}
                  />
                </RequireRole>
              }
            />
            {/* The first build step's placeholder pages are gone (owner,
          2026-09-27). Staff (support_operations) use the school account
          requests page; the other old addresses go to the normal
          workspace choice. */}
            <Route
              path="/adult/support-operations"
              element={<Navigate to="/adult/staff/school-accounts" replace />}
            />
            {/* School admin (2026-10-01): their own four pages. */}
            <Route
              path="/adult/school"
              element={
                <RequireRole auth={auth} data={data} allow="school">
                  <SchoolAdminHome
                    provider={teacherStudentsProvider}
                    auth={auth}
                    notifyStaff={(requestId) => {
                      notifySchoolRequest(requestId, 'new_request');
                    }}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/school/teachers"
              element={
                <RequireRole auth={auth} data={data} allow="school">
                  <SchoolAdminTeachers
                    provider={teacherStudentsProvider}
                    auth={auth}
                    getAccessToken={getAccessToken}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/school/reports"
              element={
                <RequireRole auth={auth} data={data} allow="school">
                  <SchoolAdminReports
                    provider={teacherStudentsProvider}
                    auth={auth}
                  />
                </RequireRole>
              }
            />
            <Route
              path="/adult/school/account"
              element={
                <RequireRole auth={auth} data={data} allow="school">
                  <YourAccount kind="school" auth={auth} data={data} />
                </RequireRole>
              }
            />
            <Route
              path="/adult/school-lead"
              element={<Navigate to="/adult/school" replace />}
            />
            {['parent-carer', 'pilot-admin'].map((slug) => (
              <Route
                key={slug}
                path={`/adult/${slug}`}
                element={<Navigate to="/adult/role-selection" replace />}
              />
            ))}
            <Route
              path="/learn"
              element={<StudentStart client={learnerSessionClient} />}
            />
            <Route
              path="/learn/session"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <SessionIntroduction client={learnerSessionClient} />
                </RequireLearnerCapability>
              }
            />
            <Route
              path="/learn/focus"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <FocusMoment />
                </RequireLearnerCapability>
              }
            />
            <Route
              path="/learn/practice"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <PracticeTask client={learnerSessionClient} />
                </RequireLearnerCapability>
              }
            />
            <Route
              path="/learn/help"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <HelpState client={learnerSessionClient} />
                </RequireLearnerCapability>
              }
            />
            <Route
              path="/learn/paused"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <PausedState client={learnerSessionClient} />
                </RequireLearnerCapability>
              }
            />
            <Route
              path="/learn/problem"
              element={
                <RequireLearnerCapability client={learnerSessionClient}>
                  <ProblemReportState client={learnerSessionClient} />
                </RequireLearnerCapability>
              }
            />
            {/* DM-IMP-005 changes-required remediation, Task 5 (finding 5,
          final resolution): the capability that reached this session's
          terminal state is revoked atomically by the server the moment
          it happens, so a RequireLearnerCapability guard here would
          always fail (it calls resume(), which requires an active
          capability) and bounce the learner away from their own
          completion/ended screen. These two screens are static and
          generic -- no session content, no learner-specific data, no
          props at all (CompletionState/EndEarlyState) -- so direct,
          unauthenticated access reveals nothing sensitive; the guard is
          neither needed nor compatible with the new terminal flow. */}
            <Route path="/learn/complete" element={<CompletionState />} />
            <Route path="/learn/ended" element={<EndEarlyState />} />
            <Route
              path="/denied"
              element={<StatePage state="permission-denied" />}
            />
            <Route
              path="/unavailable"
              element={<StatePage state="system-error" />}
            />
            <Route
              path="/expired-access"
              element={<StatePage state="expired-access" />}
            />
            <Route
              path="/safe-exit"
              element={<SafeExit client={learnerSessionClient} />}
            />
            <Route path="*" element={<StatePage state="not-found" />} />
          </Routes>
        </Suspense>
      </AlertsProviderContext.Provider>
    </SupportMessageContext.Provider>
  );
}
